import { Component, DestroyRef, HostListener, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';

import { FormsModule } from '@angular/forms';
import { DateUtil } from '../../../../util/DateUtil';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { BillingPeriod, BillingPeriodService, BillingService, LockRequest } from '@eadsysteme/billingclient-v1';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SessionService } from '../../../../services/session.service';
import { SnackbarService } from '../../../../services/snackbar.service';
import { finalize, switchMap } from 'rxjs/operators';
import { DragToCompleteComponent } from '../drag-to-complete/drag-to-complete.component';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { Observable, of, zip } from 'rxjs';
import { RealEstateService } from '@eadsysteme/ewsrealestateclient-v3';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../../../../services/config/config.service';

@Component({
	selector: 'ara-period-completion-dialog',
	templateUrl: './period-completion-dialog.component.html',
	styleUrls: ['./period-completion-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		MatDialogModule,
		MatCheckboxModule,
		MatButtonModule,
		MatIconModule,
		MatTabsModule,
		FormsModule,
		TranslocoModule,
		DragToCompleteComponent,
		CdkDrag,
		CdkDragHandle,
	],
})
export class PeriodCompletionDialogComponent implements OnInit {
	protected readonly dateUtil = inject(DateUtil);
	private readonly http = inject(HttpClient);
	private readonly configService = inject(ConfigService);
	protected sortDirection: WritableSignal<'asc' | 'desc'> = signal<'desc'>('desc');
	private readonly destroyRef = inject(DestroyRef);
	protected data = inject(MAT_DIALOG_DATA);

	protected periods: WritableSignal<BillingPeriod[]> = signal<BillingPeriod[]>([]);
	private readonly latestPeriod = signal<BillingPeriod>(undefined);
	finishedLoadingPeriods = signal(false);
	billingId: string = undefined;
	realEstateId: string = undefined;
	currentPeriod: BillingPeriod | undefined;
	public readonly dialogRef = inject(MatDialogRef<PeriodCompletionDialogComponent>);
	private readonly billingPeriodService = inject(BillingPeriodService);
	private readonly translocoService = inject(TranslocoService);
	private readonly realEstateService = inject(RealEstateService);
	private readonly billingService = inject(BillingService);
	protected readonly sessionService = inject(SessionService);
	private readonly snackBar = inject(SnackbarService);

	// Cost statement
	costGroupsCreated = false;
	costNamesUpToDate = false;
	costAllocationKeysAssigned = false;
	costExceptionsAndFlatratesCreated = false;
	invoiceItemsCreated = false;
	invoicesCreated = false;
	printOrderSorted = false;
	costItemsValidated = false;
	billingRemarksEntered = false;

	// Billing settings
	countryRegulationsSelected = false;
	taxSettingsConfigured = false;
	co2CostActConfigured = false;
	idaSettingsConfigured = false;

	// User data
	occupantChangesRecorded = false;
	occupantSharesMaintained = false;
	occupantSelectionsMaintained = false;
	advancePaymentsRecorded = false;
	occupantSettingsUpToDate = false;

	public readonly costCheckboxTotal = 9;
	public readonly settingsCheckboxTotal = 4;
	public readonly userCheckboxTotal = 5;

	get allChecked(): boolean {
		return (
			this.costGroupsCreated &&
			this.costNamesUpToDate &&
			this.costAllocationKeysAssigned &&
			this.costExceptionsAndFlatratesCreated &&
			this.invoiceItemsCreated &&
			this.invoicesCreated &&
			this.printOrderSorted &&
			this.costItemsValidated &&
			this.billingRemarksEntered &&
			this.countryRegulationsSelected &&
			this.taxSettingsConfigured &&
			this.co2CostActConfigured &&
			this.idaSettingsConfigured &&
			this.occupantChangesRecorded &&
			this.occupantSharesMaintained &&
			this.occupantSelectionsMaintained &&
			this.advancePaymentsRecorded &&
			this.occupantSettingsUpToDate
		);
	}

	ngOnInit() {
		this.billingId = this.data?.billingId;
		this.realEstateId = this.data?.realEstateId;
		this.currentPeriod = this.data?.period;
		this.loadPeriods();
	}

	get costsCheckedCount(): number {
		return [
			this.costGroupsCreated,
			this.costNamesUpToDate,
			this.costAllocationKeysAssigned,
			this.costExceptionsAndFlatratesCreated,
			this.invoiceItemsCreated,
			this.invoicesCreated,
			this.printOrderSorted,
			this.costItemsValidated,
			this.billingRemarksEntered,
		].filter(Boolean).length;
	}

	get settingsCheckedCount(): number {
		return [this.countryRegulationsSelected, this.taxSettingsConfigured, this.co2CostActConfigured, this.idaSettingsConfigured].filter(Boolean).length;
	}

	get userCheckedCount(): number {
		return [
			this.occupantChangesRecorded,
			this.occupantSharesMaintained,
			this.occupantSelectionsMaintained,
			this.advancePaymentsRecorded,
			this.occupantSettingsUpToDate,
		].filter(Boolean).length;
	}

	protected get formattedPeriodEndDate(): string {
		return this.dateUtil.setDateFormatByActiveLang(this.data?.period?.validTo, '', false, true);
	}

	protected get formattedLockDisplayDate(): string {
		const currentLockDate: string | undefined = this.data?.lockPortalAccessUntil;
		const shouldUpdateLock = !currentLockDate || (this.currentPeriod?.validTo ?? '') > currentLockDate;
		const date = shouldUpdateLock ? this.currentPeriod?.validTo : currentLockDate;
		return this.dateUtil.setDateFormatByActiveLang(date, '');
	}

	onCancel(): void {
		this.dialogRef.close(false);
	}

	@HostListener('document:keydown.escape', ['$event'])
	onEscapeKey(event: Event): void {
		event.preventDefault();
		this.onCancel();
	}

	private sortPeriods(periods: BillingPeriod[]): BillingPeriod[] {
		if (!periods) return [];
		return [...periods].sort((a, b) => {
			return this.sortDirection() === 'desc' ? b.validFrom.localeCompare(a.validFrom) : a.validFrom.localeCompare(b.validFrom);
		});
	}
	private loadPeriods(): void {
		this.finishedLoadingPeriods.set(false);
		this.billingPeriodService
			.readAllBillingPeriod(this.sessionService.effectiveArea, this.billingId)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => this.finishedLoadingPeriods.set(true))
			)
			.subscribe({
				next: (periods) => {
					this.periods.set(this.sortPeriods(periods));
					this.latestPeriod.set(periods?.sort((a, b) => b.validFrom.localeCompare(a.validFrom)).at(0));
				},
				error: () => {
					this.periods.set([]);
				},
			});
	}
	isSubmitting = signal(false);

	protected lock() {
		if (this.isSubmitting()) return;

		const currentPeriod = this.currentPeriod;
		const area = this.data?.area;
		const lockRequest: LockRequest = {
			lockPortalAccessUntil: currentPeriod.validTo,
		};

		const currentLockDate: string | undefined = this.data?.lockPortalAccessUntil;
		const shouldUpdateLock = !currentLockDate || currentPeriod.validTo > currentLockDate;

		const lockRequests: Observable<unknown>[] = [];
		if (shouldUpdateLock) {
			lockRequests.push(
				this.realEstateService.lockPortalAccessUntil(area, this.realEstateId, { lockPortalAccessUntil: lockRequest.lockPortalAccessUntil }),
				this.billingService.lockPortalAccessBilling(area, this.billingId, lockRequest)
			);
		}

		this.isSubmitting.set(true);
		this.billingPeriodService
			.submitPortalBillingPeriod(area, this.billingId, currentPeriod.exportId)
			.pipe(
				switchMap(() => (lockRequests.length ? zip(...lockRequests) : of(null))),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe({
				next: () => {
					this.snackBar.success(this.translocoService.translate('billing.importantNoticeContent.completed'));
					this.dialogRef.close({ completed: true, period: currentPeriod });
				},
				error: () => {
					this.isSubmitting.set(false);
					this.snackBar.error(this.translocoService.translate('errors.savingImportantNoticeContent'));
				},
			});
	}
}
